import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';

interface LoginFormData {
  email: string;
  password: string;
}

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const { control, handleSubmit, formState: { errors } } = useForm<LoginFormData>({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError('');
      setLoading(true);
      await login(data.email, data.password);
      // ログイン後は/adminにリダイレクト（AdminIndexRedirectで閲覧者かどうか判定して適切なページに遷移）
      navigate('/admin');
    } catch (error: any) {
      setError('ログインに失敗しました。メールアドレスとパスワードを確認してください。');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: `
          linear-gradient(135deg, #F49121 0%, #F49121 20%, #1B76D2 80%, #1B76D2 100%),
          linear-gradient(90deg, #FFFFFF 0%, #1B76D2 66%, #1B76D2 100%),
          linear-gradient(90deg, #F49121 0%, #FFFFFF 34%, #FFFFFF 100%)
        `,
        backgroundSize: '100% 100%, 100% 100%, 100% 100%',
        backgroundPosition: '0% 0%, 0% 0%, 0% 0%',
        backgroundRepeat: 'no-repeat, no-repeat, no-repeat',
        opacity: 1,
        backdropFilter: 'blur(30px)',
        WebkitBackdropFilter: 'blur(30px)',
      }}
    >
      <Container maxWidth="sm">
        {/* ロゴとタイトル */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '23px',
            mb: 4,
          }}
        >
          <img
            src="/assets/images/logo_white.svg"
            alt="Qwipe"
            style={{
              height: '56px',
              width: 'auto',
              display: 'block',
            }}
          />
          <Typography
            variant="h4"
            component="span"
            sx={{
              color: 'rgba(255, 255, 255, 0.95)',
              fontWeight: 400,
              fontSize: '43px',
              lineHeight: '56px',
              height: '56px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Admin
          </Typography>
        </Box>

        {/* ログインカード */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: '24px',
            overflow: 'hidden',
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(30px)',
            WebkitBackdropFilter: 'blur(30px)',
            border: 'none',
          }}
        >
          
          <CardContent sx={{ p: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: 'メールアドレスは必須です',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: '有効なメールアドレスを入力してください',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="メールアドレス"
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    margin="normal"
                    variant="outlined"
                    autoComplete="email"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#FFFFFF',
                        borderRadius: 2,
                        '& fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.12)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1B76D2',
                        },
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                rules={{
                  required: 'パスワードは必須です',
                  minLength: {
                    value: 6,
                    message: 'パスワードは6文字以上で入力してください',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="パスワード"
                    type={showPassword ? 'text' : 'password'}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    margin="normal"
                    variant="outlined"
                    autoComplete="current-password"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#FFFFFF',
                        borderRadius: 2,
                        '& fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.12)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1B76D2',
                        },
                      },
                    }}
                  />
                )}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={showPassword}
                    onChange={(e) => setShowPassword(e.target.checked)}
                    icon={
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '4px',
                          backgroundColor: '#FFFFFF',
                        }}
                      />
                    }
                    checkedIcon={
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '4px',
                          backgroundColor: '#F49121',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 14, color: '#FFFFFF' }} />
                      </Box>
                    }
                  />
                }
                label="パスワードを表示する"
                sx={{ mt: 1 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 500,
                  borderRadius: 2,
                  backgroundColor: '#1B76D2',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#0062C6',
                  },
                  '&:disabled': {
                    backgroundColor: '#9E9E9E',
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'ログイン'
                )}
              </Button>

            </Box>
          </CardContent>
        </Paper>

        {/* デモ用の認証情報表示（コメントアウト） */}
        {/* <Paper
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(30px)',
            WebkitBackdropFilter: 'blur(30px)',
            borderRadius: 2,
            border: '1px solid rgba(255, 255, 255, 0.3)',
          }}
        >
          <Typography variant="body2" align="center" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
            <strong>デモ用ログイン情報:</strong><br />
            メール: admin@example.com<br />
            パスワード: password123
          </Typography>
        </Paper> */}
      </Container>
    </Box>
  );
};
